import React, { useEffect, useState } from 'react';
import { owner, engagement } from '../services/api';
import { toast } from '../hooks/use-toast';
import { QrCode, Mail, Copy, Printer, Loader2, Send, ShieldCheck, ExternalLink } from 'lucide-react';
import TierGate from '../components/TierGate';

/** Owner-only: shows the "Review Check-in QR" for customers to scan in-store,
 *  and lets the owner email a single-use review invite to a specific customer. */
export default function BusinessReviews() {
  return (
    <TierGate feature="reviews_verification">
      <BusinessReviewsInner />
    </TierGate>
  );
}

function BusinessReviewsInner() {
  const [myBiz, setMyBiz] = useState([]);
  const [selectedBiz, setSelectedBiz] = useState('');
  const [loading, setLoading] = useState(true);
  const [ownerErr, setOwnerErr] = useState('');
  const [showQr, setShowQr] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [sending, setSending] = useState(false);
  const [lastInvite, setLastInvite] = useState(null);

  useEffect(() => {
    owner.myBusinesses().then(bs => {
      setMyBiz(bs);
      if (bs.length) setSelectedBiz(bs[0].id);
    }).catch(err => {
      setOwnerErr(err?.response?.status === 403 ? 'This page is for business owners. Add your first listing to unlock review verification tools.' : (err?.response?.data?.detail || 'Failed to load'));
    }).finally(() => setLoading(false));
  }, []);

  const biz = myBiz.find(b => b.id === selectedBiz);
  const checkinUrl = biz ? `${window.location.origin}/reviews/checkin/${biz.id}` : '';
  const qrImgUrl = checkinUrl ? `https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=8&data=${encodeURIComponent(checkinUrl)}` : '';

  const copy = async (text) => { try { await navigator.clipboard.writeText(text); toast({ title: 'Copied' }); } catch {} };
  const print = () => {
    const w = window.open('', '_blank'); if (!w || !biz) return;
    w.document.write(`<html><head><title>${biz.name} — Review QR</title><style>body{font-family:system-ui;text-align:center;padding:40px;color:#111}h1{margin:0 0 8px}img{margin:16px auto;display:block}p{color:#555}</style></head><body><h1>Loved your visit?</h1><h2>${biz.name}</h2><p>Scan this QR to leave a verified review.</p><img src="${qrImgUrl.replace('320x320','520x520')}" alt="QR"/><p style="font-size:12px;color:#999">${checkinUrl}</p></body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail) return;
    setSending(true);
    try {
      const res = await engagement.inviteReview(selectedBiz, inviteEmail, inviteName || null);
      setLastInvite(res.invite);
      toast({ title: 'Invite sent!', description: `Emailed ${inviteEmail}.` });
      setInviteEmail(''); setInviteName('');
    } catch (err) {
      toast({ title: 'Send failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setSending(false); }
  };

  if (loading) return <Loader2 className="animate-spin text-gray-400" size={16}/>;
  if (ownerErr) return <div className="pbb-card p-6 text-sm text-gray-400" data-testid="owner-gate">{ownerErr}</div>;
  if (!myBiz.length) return <div className="pbb-card p-6 text-sm text-gray-400">Add a business listing first to manage reviews.</div>;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold inline-flex items-center gap-2"><ShieldCheck className="text-green-400" size={22}/> Review Verification</h1>
        <p className="text-gray-400 text-sm">Only verified customers can leave reviews. Display the QR code at your counter, or email a single-use invite link.</p>
      </div>

      {myBiz.length > 1 && (
        <select value={selectedBiz} onChange={e => setSelectedBiz(e.target.value)} className="pbb-input max-w-sm">
          {myBiz.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {/* Option A — QR at counter */}
        <div className="pbb-card p-5 space-y-3" data-testid="reviews-qr-block">
          <div className="flex items-center gap-2 font-semibold"><QrCode size={16} className="text-green-400"/> Option A · In-store QR</div>
          <p className="text-xs text-gray-400">Print & display at your counter. Customers who scan get verified via their device location (within 500 m of your listed address).</p>
          <button onClick={() => setShowQr(true)} data-testid="reviews-show-qr" className="pbb-btn-green inline-flex items-center gap-2"><QrCode size={14}/> Show QR</button>
          <div className="text-[11px] text-gray-500 break-all bg-[#0d1f14] border border-[#153b23] rounded px-2 py-1.5">{checkinUrl}</div>
        </div>

        {/* Option B — Email invite */}
        <div className="pbb-card p-5 space-y-3" data-testid="reviews-invite-block">
          <div className="flex items-center gap-2 font-semibold"><Mail size={16} className="text-green-400"/> Option B · Email invite</div>
          <p className="text-xs text-gray-400">Email a specific customer a single-use link. No location check — works for online-only or after-service reviews.</p>
          <form onSubmit={sendInvite} className="space-y-2">
            <input type="email" required placeholder="Customer email *" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} className="pbb-input" data-testid="invite-email"/>
            <input placeholder="Customer name (optional)" value={inviteName} onChange={e => setInviteName(e.target.value)} className="pbb-input"/>
            <button type="submit" disabled={sending} data-testid="invite-send" className="pbb-btn-green w-full inline-flex items-center justify-center gap-2 disabled:opacity-70">
              {sending ? <Loader2 className="animate-spin" size={14}/> : <Send size={14}/>} Send invite
            </button>
          </form>
          {lastInvite && (
            <div className="rounded-lg bg-[#0d1f14] border border-[#153b23] p-3 text-xs space-y-1" data-testid="last-invite">
              <div className="text-green-400 font-semibold">✓ Sent to {lastInvite.customerEmail}</div>
              <div className="text-gray-400">Link (in case they need it):</div>
              <div className="flex gap-2">
                <div className="flex-1 truncate bg-black/40 rounded px-2 py-1.5 text-gray-300">{lastInvite.link}</div>
                <button onClick={() => copy(lastInvite.link)} className="px-2 py-1.5 rounded bg-green-600 text-white inline-flex items-center gap-1"><Copy size={12}/></button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showQr && biz && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowQr(false)}>
          <div className="pbb-card w-full max-w-md p-5" onClick={e => e.stopPropagation()} data-testid="reviews-qr-modal">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold text-lg inline-flex items-center gap-2"><QrCode size={18} className="text-green-400"/> Review Check-in QR</div>
              <button onClick={() => setShowQr(false)} className="text-gray-400">✕</button>
            </div>
            <div className="rounded-xl bg-white p-4 flex items-center justify-center">
              <img src={qrImgUrl} alt="Review QR" className="w-64 h-64"/>
            </div>
            <div className="mt-3 text-sm">
              <div className="font-semibold">{biz.name}</div>
              <div className="text-gray-400 text-xs">Scan → verify location → leave a review</div>
              <div className="mt-2 text-[11px] text-gray-500 break-all bg-[#0d1f14] border border-[#153b23] rounded px-2 py-1.5">{checkinUrl}</div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-4">
              <button onClick={() => copy(checkinUrl)} className="px-3 py-2 rounded-lg border border-[#2a2a2a] text-sm inline-flex items-center justify-center gap-1"><Copy size={14}/> Copy link</button>
              <button onClick={print} className="px-3 py-2 rounded-lg bg-green-600 text-white text-sm inline-flex items-center justify-center gap-1"><Printer size={14}/> Print</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
